import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from '../lib/routing';
import { useAuth } from '../hooks/use-auth';
import { useRoles } from '../hooks/use-roles';
import { resolveAppDestination } from '../lib/role-home';
import FullPageSpinner from './full-page-spinner';
import AdminAccessDenied from './admin-access-denied';

interface CheckedRouteProps {
  /** Async predicate: resolves to true if the current user may access the route. */
  checkFn: () => Promise<boolean>;
  children: ReactNode;
}

/**
 * Shared guard for routes that require an async role check (e.g. admin or staff).
 * While the check is in-flight (null) it shows a spinner instead of redirecting,
 * so the user is never bounced to /app before the check resolves.
 */
export default function CheckedRoute({ checkFn, children }: CheckedRouteProps) {
  const { session, loading: authLoading } = useAuth();
  const { activeRole, available, loading: rolesLoading } = useRoles();
  /* null = checking, true/false = resolved */
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!session) {
      setAllowed(false);
      return;
    }
    setAllowed(null); /* reset to "checking" while async runs */
    let cancelled = false;
    (async () => {
      /* INVARIANT : `allowed` doit quitter l'état « en cours » quoi qu'il
         arrive. Un rejet ici (panne réseau, erreur serveur, prédicat qui lève
         avant de rendre sa promesse) laisserait la route sur son écran de
         chargement, définitivement. Le repli est le refus : accorder l'accès
         sur une panne ouvrirait une route d'administration à qui n'y a pas
         droit, et un refus reste rattrapable par un rechargement. */
      try {
        const ok = await checkFn();
        if (!cancelled) setAllowed(ok);
      } catch {
        if (!cancelled) setAllowed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, authLoading, checkFn]);

  if (authLoading || allowed === null || (allowed === false && rolesLoading)) return <FullPageSpinner />;
  if (!session) return <Navigate to="/sign-in" replace />;
  if (!allowed) {
    const destination = resolveAppDestination(activeRole, available);
    if (destination.kind === 'home') return <AdminAccessDenied homePath={destination.path} />;
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}
